import { cx } from '@chakra-ui/utils';
import { useContext } from 'react';
import type {
  OptionTypeBase,
  Props as ReactSelectProps,
  ValueContainerProps as ReactSelectValueContainerProps,
} from 'react-select';

import {
  Box,
  type BoxProps,
  Center,
  TagGroup,
  type TagGroupProps,
  TAG_GROUP_GAPS,
  useTheme,
} from '@frontend/components';
import {
  AssistanceApprovedIcon,
  AssistanceAvailableIcon,
  AssistanceRejectedIcon,
} from '@frontend/components/src/components/mediaAndIcons/icons/iconSet';

import { SelectContext } from '../SelectContext';
import { useSelectStyles } from '../SelectStylesContext';
import { BASE_CLASS_NAME } from '../const';

export type ValueContainerProps<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
> = ReactSelectValueContainerProps<OptionType, IsMulti> & {
  innerProps?: BoxProps;
  selectProps: ReactSelectProps<OptionType, IsMulti>;
};

export const ValueContainer = <
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
>({
  children,
  innerProps,
  selectProps,
  hasValue,
}: ValueContainerProps<OptionType, IsMulti>) => {
  const theme = useTheme();
  const { valueContainer: valueContainerStyles } = useSelectStyles();

  const { hasSuggestions, hasAcceptedSuggestions, hasRejectedSuggestions } =
    useContext(SelectContext);

  const shouldShowAvailableIcon =
    hasSuggestions && !hasAcceptedSuggestions && !hasRejectedSuggestions;
  const shouldShowApprovedIcon = hasAcceptedSuggestions;
  const shouldShowRejectedIcon =
    hasSuggestions && hasRejectedSuggestions && !hasAcceptedSuggestions;

  const tagGroupSize: TagGroupProps['size'] =
    selectProps.size === 'sm' ? 'xs' : 'sm';

  const multiSelectProps: BoxProps & TagGroupProps = {
    as: TagGroup,
    size: tagGroupSize,
    paddingY: hasValue
      ? `calc(${theme.space[TAG_GROUP_GAPS[tagGroupSize]]} - 1px)`
      : undefined,
  };

  return (
    <Box
      {...innerProps}
      className={cx(
        `${BASE_CLASS_NAME}__value-container`,
        innerProps?.className,
      )}
      {...(selectProps.isMulti ? multiSelectProps : null)}
      __css={valueContainerStyles}
    >
      {children}
      {shouldShowAvailableIcon ||
      shouldShowApprovedIcon ||
      shouldShowRejectedIcon ? (
        <Center
          flex={1}
          justifyContent="flex-end"
          flexShrink={0}
          position="relative"
          zIndex={2}
        >
          {shouldShowAvailableIcon ? (
            <AssistanceAvailableIcon boxSize={3} color="alert.assistance" />
          ) : null}
          {shouldShowApprovedIcon ? (
            <AssistanceApprovedIcon boxSize={3} color="alert.assistance" />
          ) : null}
          {shouldShowRejectedIcon ? (
            <AssistanceRejectedIcon boxSize={3} color="feature.placeholder" />
          ) : null}
        </Center>
      ) : null}
    </Box>
  );
};
